import { IGlobal } from "config/config.interface";
import { WindowContainer } from "lib/interfaces/user.interface";
import { ApiBus } from "lib/services/api.bus";
import { NewApiService } from "lib/services/api/newapi.service";
import { LoaderService } from "lib/services/loader.service";
import { ModalService } from "lib/services/modal.service";
import { NavigationService } from "lib/services/navigation.service";
import { ToastService } from "lib/services/toast.service";
import { IAppTranslations } from "lib/translations/interfaces/translations.interface";
import * as _ from "lodash";
import { EMPTY, forkJoin, Observable, of, Subject, throwError, timer } from "rxjs";
import { AjaxResponse } from "rxjs/ajax";
import { catchError, map, switchMap } from "rxjs/operators";
import { UnavailableAssessmentModal } from "../components/modals/unavailable-assessment.modal";
import { UnsupportedLanguageModal } from "../components/modals/unsupported-language.model";
import { WelcomeBack } from "../components/modals/welcome-back.modal";
import { cleanApiResponse, getQuestionUri } from "../components/questions/question-helpers";
import { IAssessment, IAssessmentLanguageResponse, IAssessmentPurposeResponse, IAssessmentQuestion, ICanShowDemoResponse, IDemographicsOptions } from "../interfaces/assessment.interface";
import { AssessmentUpdateQueue } from './assessment-update-queue.service';

export class AssessmentService {
    _apiService: NewApiService;
    apiBus: ApiBus;
    globalConfig = (global as any as IGlobal);
    toastService: ToastService;
    loaderService: LoaderService;
    _assessment: IAssessment;
    _assessmentSubject: Subject<IAssessment>;
    _assessmentUpdateQueue: AssessmentUpdateQueue;
    _questionBus: Subject<IAssessmentQuestion>;
    _modalService: ModalService;
    _navService: NavigationService;

    constructor(private readonly translations: IAppTranslations) {
        this._apiService = new NewApiService();
        this.apiBus = WindowContainer.GetWindow().getApiBus();
        this.toastService = new ToastService();
        this.loaderService = new LoaderService();
        this._assessment = {} as IAssessment;
        this._assessmentSubject = new Subject<IAssessment>();
        this._assessmentUpdateQueue = new AssessmentUpdateQueue(this._apiService);
        this._questionBus = new Subject();
        this._modalService = new ModalService();
        this._navService = new NavigationService();
    }

    public GetAssessmentData = (participantId: number, pqpId: number, cultureId: number): Observable<AjaxResponse<IAssessment>> => {
        return this._apiService.get<IAssessment>({
            url: "/Assessment/GetAssessmentInfo",
            params: {
                participantId,
                pqpId,
                cultureId
            }
        }).pipe(
            switchMap(this.checkIfAssessmentIsAvailable),
            switchMap(response => {
                return this._apiService.get<ICanShowDemoResponse>({ url: "/Assessment/CanShowDemographics", params: { PQPID: pqpId } })
                    .pipe(map(($demo) => {
                        response.response.ShowDemographics = $demo.response.ShowDemographicsStatusID
                        return response;
                    }))
            }),
            switchMap((response) => this._assessmentUpdateQueue.postQueue().pipe(map(() => response))),
            switchMap(response => {
                return this._apiService.get<IAssessmentQuestion[]>(
                    {
                        url: "/Assessment/GetAssessment",
                        params: {
                            pqpId,
                            participantId,
                            targetParticipantId: response.response.TargetParticipantID,
                            showDemo: this.globalConfig.config.MRG_TRUE,
                            cultureId
                        }
                    })
                    .pipe(
                        map(questions => {
                            response.response.DemographicQuestions = questions.response;
                            return response;
                        }))
            }),
            switchMap(response => {
                return this._apiService.get<IAssessmentQuestion[]>(
                    {
                        url: "/Assessment/GetAssessment",
                        params:
                        {
                            pqpId,
                            participantId,
                            targetParticipantId: response.response.TargetParticipantID,
                            showDemo: this.globalConfig.config.MRG_FALSE,
                            cultureId
                        }
                    })
                    .pipe(
                        map(questions => {
                            response.response.Questions = questions.response.map((question) => {
                                return {
                                    ...question,
                                    ChangeEventHandler: (data: any, event?: any) => { this.handleSaveChange(data, event) },
                                }
                            });

                            response.response.Questionnaires = _.toPairs(_.groupBy(response.response.Questions, 'QuestionnaireID_FK')).map(data => {
                                return {
                                    QuestionnaireID_FK: data[0],
                                    Questions: data[1],
                                    QuestionnaireTypeID_FK: _.uniq(data[1].map(t => t.QuestionnaireTypeID_FK))[0],
                                    SortOrder: _.uniq(data[1].map(t => t.QuestionnaireSortOrder))[0],
                                }
                            }) as any
                            return response;
                        }))
            }),
            switchMap(response => {
                return this._apiService.get<IAssessmentPurposeResponse>(
                    {
                        url: "/Assessment/GetAssessmentPurpose",
                        params:
                        {
                            PQPID: pqpId,
                            ProductID: response.response.ProductID,
                            QuestionnaireID: this.getQuestionnaireId(this.canShowDemo(response.response.ShowDemographics)),
                            CultureID: cultureId
                        }
                    })
                    .pipe(map(purpose => {
                        response.response.Purpose = cleanApiResponse(purpose.response.Purpose);
                        return response;
                    }))
            }),
            switchMap(response => {
                return this._apiService.get<IAssessmentLanguageResponse[]>(
                    {
                        url: "/Assessment/GetAssessmentLanguages",
                        params:
                        {
                            PQPID: pqpId
                        }
                    })
                    .pipe(map(languages => {
                        response.response.IsSupportedLanguage = languages.response.find((r: any) => r.CultureCode === this.globalConfig.store.getState().user.participantLanguageCode) !== undefined;
                        return response;
                    }))
            }),
            // This is where we will do our final bit of data massaging before returned to the component
            map(response => {
                const nonDemoQ = response.response.Questions.filter((q) => !this.globalConfig.config.QUESTIONNAIRE_ID_PRODUCT_DEMOGRAPHICS.includes(+q.QuestionnaireID_FK))
                const answeredQ = nonDemoQ.filter((q) => q.Answered === this.globalConfig.config.QUESTION_FULLYANSWERED);
                response.response.TotalQuestions = nonDemoQ.length;
                response.response.CompletedQuestions = answeredQ.length;
                response.response.PercentDone = (Math.floor((answeredQ.length / nonDemoQ.length) * 100)).toString();
                response.response.ShouldWelcomeBack = response.response.CompletedQuestions > 0 && response.response.CompletedQuestions !== response.response.TotalQuestions
                response.response.Culture = cultureId;

                this._assessmentUpdateQueue.start();

                return response;
            }),
            switchMap(this.checkIfWelcomeBack),
            switchMap(this.checkIfLanguageSupported)
        ) as any;
    }


    public getAssessmentInstructions = (PQPID: number, ProductID: number, QuestionnaireID: number, CultureID: number): Observable<any> => {
        return this._apiService.get(
            {
                url: '/Assessment/GetAssessmentInstructions',
                params: {
                    PQPID,
                    ProductID,
                    QuestionnaireID,
                    CultureID
                }
            })
            .pipe(
                map((response) => {
                    return response.response;
                })
            )
    }

    public storeResponses = () => {
        if (_.values(this.apiBus.getItems()).length === 0) {
            return of([]);
        }
        return this.apiBus.execute()
            .pipe(
                catchError(error => {
                    var qp = error.request && error.request.queryParams ? error.request.queryParams.QuestionID.toString() : "";
                    this.apiBus.reQueueItem(error, qp);
                    return of(error);
                }),
                map(response => {
                    response.forEach((item: any) => {
                        this.apiBus.removeItem(item, (item as any).request.queryParams.QuestionID);
                    })
                    return response;
                })
            );
    }

    public getQuestionResponses = (question: IAssessmentQuestion) => {
        const apiUri = getQuestionUri(question.QuestionTypeID_FK);
        const apiReq = {
            PQPID: question.ParticipantToQuestionnaireToProjectID_PK,
            QuestionID: question.QuestionID_FK,
            QuestionnaireID: question.QuestionnaireID_FK,
            ParticipantID: question.ParticipantID_FK,
            CultureID: question.CultureID_FK,
            TargetParticipantID: question.TargetParticipantID_FK
        }
        question.ApiUri = apiUri;

        if (apiUri && apiUri.answer) {
            return forkJoin([this._apiService.get<any>({ url: `/Assessment/${apiUri.answer}`, params: apiReq })
                .pipe(
                    map((response) => {
                        question.Responses = response.response;
                        return question;
                    })
                ),
            this._apiService.get<IDemographicsOptions[]>({ url: `/Assessment/${apiUri.question}`, params: apiReq })
                .pipe(
                    map((response) => {
                        if (this.needsOptionsMapped(question.QuestionTypeID_FK)) {
                            question.Options = response.response.map((r: IDemographicsOptions) => {
                                return {
                                    Id: r.Id,
                                    OptionOrdinal: r.OptionOrdinal,
                                    TextValue: r.TextValue,
                                    Value: r.OptionOrdinal,
                                    QuestionOptionStatement: r.QuestionOptionStatement,
                                    QuestionStatement: r.QuestionStatement

                                }
                            })
                        }
                        question.Question = response.response;
                        return question;
                    })
                )
            ])
        } else {
            return EMPTY;
        }
    }

    // public ListenForTab = () => {
    //     return fromEvent(document, 'keyup').pipe(filter(event => (event as any).which === 9));
    // }

    public SubmitAssessment(assessment: IAssessment) {
        this.loaderService.show();
        return timer(1000)
            .pipe(
                switchMap(() => this.storeResponses()),
                switchMap(() => this.CallSubmitAssessment(assessment.PQPID))
            )
    }

    private CallSubmitAssessment(PQPID: number) {
      this._apiService.setOptions({handleErrors: false});
        return this._apiService.post({ url: "/Assessment/SubmitAssessment", params: { PQPID: PQPID } })
          .pipe(
            map(response => {
              if (response.response === 0) {
                throw new Error('Response contains 0');
              }
              return response;
            }),
            catchError(error => {
              console.error(error);
              return throwError(error);
            })
          )
    }

    public StoreAssessment(assessment: IAssessment): void {
        this._assessment = assessment;
        if (!assessment.hasOwnProperty('AssessmentName')) {
            return;
        }
        this._assessmentSubject.next({ ...assessment });
    }

    public GetAssessmentFromStore(): Observable<IAssessment> {
        return this._assessmentSubject;
    }

  public handleSaveBuildEvent = (data: IAssessmentQuestion) => {
    if (data.QuestionTypeID_FK === this.globalConfig.config.QUESTION_TYPE_FC) {
      return this.handleSaveChange(null, data);
    }
    if (data.QuestionTypeID_FK === this.globalConfig.config.QUESTION_TYPE_AR) {
      return this.handleSaveChange((data.Responses as any).Value, data);
    }
    if (data.QuestionTypeID_FK === this.globalConfig.config.QUESTION_TYPE_OE) {
      return this.handleSaveChange({text: (data.Responses as any).TextValue}, data);
    }
    if (data.QuestionTypeID_FK === this.globalConfig.config.QUESTION_TYPE_MS) {
      return this.handleSaveChange({
        text: (data.Responses as any).TextValue,
        value: (data.Responses as any).ResponseValue
      }, data);

    }
    if (data.QuestionTypeID_FK === this.globalConfig.config.QUESTION_TYPE_MC) {
      return this.handleSaveChange({
        text: (data.Responses as any).TextValue,
        value: (data.Responses as any).Value
      }, data);
    }
  }

    private checkIfWelcomeBack = (ajaxResponse: AjaxResponse<IAssessment>) => {
        const data = new Subject<AjaxResponse<IAssessment>>();

        if (ajaxResponse.response.ShouldWelcomeBack) {
            const dialog = new WelcomeBack(this.translations).build();
            dialog.subscribe((response: any) => {
                response.buttons.close.callback = () => {
                    data.next(ajaxResponse);
                    data.complete();
                    return data;
                };
                this._modalService.displayDialog(response);
            })
        } else {
            return of(ajaxResponse);
        }
        return data;
    }

    private checkIfLanguageSupported = (ajaxResponse: AjaxResponse<IAssessment>) => {
        const data = new Subject<AjaxResponse<IAssessment>>();
        if (ajaxResponse.response.CompletedQuestions === 0 && !ajaxResponse.response.IsSupportedLanguage) {
            const dialog = new UnsupportedLanguageModal(this.translations).build(ajaxResponse.response.AssessmentName)
            dialog.subscribe((response: any) => {
                response.buttons.close.callback = () => {
                    data.next(ajaxResponse);
                    data.complete();
                    return data;
                };
                this._modalService.displayDialog(response);
            })
        } else {
            return of(ajaxResponse);

        }
        return data;
    }

    private checkIfAssessmentIsAvailable = (ajaxResponse: AjaxResponse<IAssessment>) => {
        const data = new Subject<AjaxResponse<IAssessment>>();
        if (!ajaxResponse.response.AssessmentName) {
            const dialog = new UnavailableAssessmentModal().build()
            dialog.subscribe((response: any) => {
                response.buttons.close.callback = () => {
                    this._navService.NavigateTo("assessments", true)
                    data.next(ajaxResponse);
                    data.complete();
                    return data;
                };
                this._modalService.displayDialog(response);
            })
        } else {
            return of(ajaxResponse);

        }
        return data;
    }

    private getQuestionnaireId = (showDemo: number): number => {
        return showDemo === this.globalConfig.config.MRG_TRUE ? this.globalConfig.config.QUESTIONNAIRE_ID_DEMOGRAPHICS : 0;
    }

    private canShowDemo = (ShowDemographicsStatusID: number): number => {
        let canShowDemo = false;
        switch (ShowDemographicsStatusID) {
            case this.globalConfig.config.DEMOGRAPHICS_QUESTIONNAIRE_COMPLETED:
            case this.globalConfig.config.DEMOGRAPHICS_QUESTIONNAIRE_SAME_PROJECT:
                canShowDemo = false;
                break;
            case this.globalConfig.config.DEMOGRAPHICS_QUESTIONNAIRE_DIFFERENT_PROJECT:
                canShowDemo = true;
                break;
            case this.globalConfig.config.DEMOGRAPHICS_QUESTIONNAIRE_NEW:
            default:
                canShowDemo = true;
                break;
        }
        return canShowDemo ? this.globalConfig.config.MRG_TRUE : this.globalConfig.config.MRG_FALSE;
    }

    private handleSaveChange = (event: any, data: IAssessmentQuestion) => {
        let options = {}
        // Determine question type and structure API call
        if (data.QuestionTypeID_FK === this.globalConfig.config.QUESTION_TYPE_FC) {
            const optionA = data.Question.find(question => question.OptionOrdinal === 1);
            const optionB = data.Question.find(question => question.OptionOrdinal === 2);
            const optionC = data.Question.find(question => question.OptionOrdinal === 3);
            if (!optionA || !optionB || !optionC) return;
            options = {
                OptionA: optionA.Value,
                OptionB: optionB.Value,
                OptionC: optionC.Value,
            }
            if (Object.values(options).filter(t => t !== 0).length > 1) {
                data.Answered = 1;
            } else {
                data.Answered = 0;
            }
        }
        if (data.QuestionTypeID_FK === this.globalConfig.config.QUESTION_TYPE_AR) {
            options = {
                ResponseValue: event
            }
            if (event > 0) {
                data.Answered = 1;
            } else {
                data.Answered = 0;
            }
          data.Responses = {Value: event} as any;

        }
        if (data.QuestionTypeID_FK === this.globalConfig.config.QUESTION_TYPE_OE) {
            if (event.text && event.text.length > 0) {
                data.Answered = 1;
              data.Responses = {
                TextValue: event.text,
                Value: 0
              } as any;
                options = {
                    ResponseValue: 0,
                    TextValue: event.text
                }
            } else {
                data.Answered = 0;
                data.Assessment.UpdatePercentage(data, this._assessment);
                return;
            }
        }
        if (data.QuestionTypeID_FK === this.globalConfig.config.QUESTION_TYPE_MS) {
            if (!event) return;
            options = {
                OptionOrdinal: event.value,
                Value: event.value,
                TextValue: event.text
            }
            data.Responses = {
              OptionOrdinal: event.value,
              Value: event.value,
              TextValue: event.text
            } as any;
        }
        if (data.QuestionTypeID_FK === this.globalConfig.config.QUESTION_TYPE_MC) {
            const optionOrdinal = data.Options.findIndex(option => option.OptionOrdinal === +event.value);
            options = {
                OptionOrdinal: optionOrdinal,
                ResponseValue: event.value,
                TextValue: event.text
            }
            data.Responses = {
              OptionOrdinal: optionOrdinal,
              Value: event.value,
              TextValue: event.text
            } as any;
            if(event.value)
              data.Answered = 1;
        }

        const params = {
            PQPID: data.ParticipantToQuestionnaireToProjectID_PK,
            QuestionID: data.QuestionID_FK,
            QuestionnaireID: data.QuestionnaireID_FK,
            ParticipantID: data.ParticipantID_FK,
            CultureID: data.CultureID_FK
        };

        this._assessmentUpdateQueue.enqueue({
            QuestionType: data.QuestionTypeID_FK,
            ...params,
            ...options
        });

        const assessmentQ = this._assessment.Questions.find(q => q.QuestionID_FK === data.QuestionID_FK);
        if (assessmentQ) {
            assessmentQ.Skipped = data.Answered !== 1;
            assessmentQ.Answered = data.Answered;
        }
        this.StoreAssessment(this._assessment);
        data.Assessment.UpdatePercentage(data, this._assessment);

        if (assessmentQ)
            this._questionBus.next(assessmentQ);
    }

    private needsOptionsMapped = (typeId: number) => {
        return (typeId === this.globalConfig.config.QUESTION_TYPE_MC) || typeId === this.globalConfig.config.QUESTION_TYPE_MS;
    }

    public isSectionComplete = (pqpId: number, sectionId: string) => {
        this._apiService.setOptions({handleErrors: false});
        return this._apiService.post({ url: "/Assessment/IsSectionComplete", params: { PQPID: pqpId, QuestionnaireList: sectionId } })
          .pipe(
            map(response => {
              return response;
            }),
            catchError(error => {
              console.error(error);
              return throwError(error);
            })
          )
    }

}
